import { Injectable } from '@angular/core';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import minMax from 'dayjs/plugin/minMax';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(minMax);

export interface TimePeriod {
	start: Dayjs;
	end: Dayjs;
}

export interface TimePeriodWithId extends TimePeriod {
	id?: string;
}

@Injectable({
	providedIn: 'root',
})
export class TimePeriodService {
	/**
	 * Merges overlapping or adjacent time periods into consolidated periods.
	 * Adjacent periods are those where one ends the day before the next begins.
	 *
	 * @param periods - Array of time periods to merge
	 * @returns Array of merged, non-overlapping time periods sorted by start date
	 */
	mergeTimePeriods(periods: TimePeriod[]): TimePeriod[] {
		if (!periods || periods.length === 0) {
			return [];
		}

		const sortedPeriods = this.sortPeriodsByStart(periods);
		const merged: TimePeriod[] = [this.clonePeriod(sortedPeriods[0])];

		for (let i = 1; i < sortedPeriods.length; i++) {
			const current = sortedPeriods[i];
			const lastMerged = merged.at(-1);

			if (this.arePeriodsOverlappingOrAdjacent(current, lastMerged)) {
				this.extendPeriodEnd(lastMerged, current.end);
			} else {
				merged.push(this.clonePeriod(current));
			}
		}

		return merged;
	}

	/**
	 * Calculates gaps (unoccupied periods) between merged occupied periods.
	 *
	 * @param mergedOccupiedPeriods - Array of merged occupied time periods
	 * @returns Array of gap periods between the occupied periods
	 */
	calculateGapsBetweenPeriods(mergedOccupiedPeriods: TimePeriod[]): TimePeriod[] {
		const gaps: TimePeriod[] = [];

		for (let i = 1; i < mergedOccupiedPeriods.length; i++) {
			const previousPeriodEnd = mergedOccupiedPeriods[i - 1].end;
			const currentPeriodStart = mergedOccupiedPeriods[i].start;

			const gapStart = previousPeriodEnd.add(1, 'day');
			const gapEnd = currentPeriodStart.subtract(1, 'day');

			if (gapStart.isSameOrBefore(gapEnd)) {
				gaps.push({ start: gapStart, end: gapEnd });
			}
		}

		return gaps;
	}

	/**
	 * Calculates parts of initial gaps that are not covered by covering periods.
	 *
	 * @param initialGaps - Array of gap periods to check
	 * @param coveringPeriods - Array of periods that may cover the gaps
	 * @returns Array of uncovered gap portions
	 */
	calculateUncoveredGaps(initialGaps: TimePeriod[], coveringPeriods: TimePeriod[]): TimePeriod[] {
		let uncoveredParts: TimePeriod[] = [...initialGaps];

		for (const cover of coveringPeriods) {
			uncoveredParts = this.subtractCoverFromGaps(uncoveredParts, cover);
		}

		return this.filterAndSortValidPeriods(uncoveredParts);
	}

	/**
	 * Calculates available (unoccupied) periods within a given overall range.
	 *
	 * @param occupiedPeriods - Array of occupied time periods
	 * @param overallStart - Start of the overall range
	 * @param overallEnd - End of the overall range
	 * @returns Array of available periods within the range
	 */
	calculateAvailablePeriods(occupiedPeriods: TimePeriod[], overallStart: Dayjs, overallEnd: Dayjs): TimePeriod[] {
		const available: TimePeriod[] = [];
		let currentStart = overallStart;

		for (const occupied of occupiedPeriods) {
			if (currentStart.isBefore(occupied.start)) {
				available.push({
					start: currentStart,
					end: occupied.start.subtract(1, 'day'),
				});
			}

			currentStart = occupied.end.add(1, 'day');

			if (currentStart.isBefore(overallStart)) {
				currentStart = overallStart;
			}
		}

		if (currentStart.isSameOrBefore(overallEnd)) {
			available.push({ start: currentStart, end: overallEnd });
		}

		return available.filter((p) => p.start.isSameOrBefore(p.end));
	}

	/**
	 * Checks if two time periods overlap.
	 *
	 * @param period1 - First time period
	 * @param period2 - Second time period
	 * @returns True if the periods overlap
	 */
	doPeriodsOverlap(period1: TimePeriod, period2: TimePeriod): boolean {
		return period1.start.isSameOrBefore(period2.end) && period1.end.isSameOrAfter(period2.start);
	}

	/**
	 * Checks if a date falls within a time period (inclusive).
	 *
	 * @param date - The date to check
	 * @param period - The time period
	 * @returns True if the date is within the period
	 */
	isDateInPeriod(date: Dayjs, period: TimePeriod): boolean {
		return (date.isAfter(period.start) || date.isSame(period.start)) && (date.isBefore(period.end) || date.isSame(period.end));
	}

	/**
	 * Sorts time periods by their start date in ascending order.
	 *
	 * @param periods - Array of time periods to sort
	 * @returns New sorted array of time periods
	 */
	sortPeriodsByStart(periods: TimePeriod[]): TimePeriod[] {
		return [...periods].sort((a, b) => a.start.diff(b.start));
	}

	/**
	 * Creates a shallow clone of a time period.
	 *
	 * @param period - The time period to clone
	 * @returns A new time period object with the same values
	 */
	private clonePeriod(period: TimePeriod): TimePeriod {
		return { start: period.start, end: period.end };
	}

	/**
	 * Checks if a period overlaps with or is adjacent to another period.
	 *
	 * @param current - The current period to check
	 * @param reference - The reference period to compare against
	 * @returns True if periods overlap or are adjacent
	 */
	private arePeriodsOverlappingOrAdjacent(current: TimePeriod, reference: TimePeriod): boolean {
		return current.start.isSameOrBefore(reference.end.add(1, 'day'));
	}

	/**
	 * Extends the end date of a period if the new end is later.
	 *
	 * @param period - The period to potentially extend
	 * @param newEnd - The potential new end date
	 */
	private extendPeriodEnd(period: TimePeriod, newEnd: Dayjs): void {
		if (newEnd.isAfter(period.end)) {
			period.end = newEnd;
		}
	}

	/**
	 * Subtracts a covering period from a list of gaps.
	 *
	 * @param gaps - Array of gap periods
	 * @param cover - The covering period to subtract
	 * @returns Array of remaining uncovered gap portions
	 */
	private subtractCoverFromGaps(gaps: TimePeriod[], cover: TimePeriod): TimePeriod[] {
		const result: TimePeriod[] = [];

		for (const gap of gaps) {
			const overlap = this.doPeriodsOverlap(gap, cover);

			if (!overlap) {
				result.push(gap);
				continue;
			}

			// Add part of gap before the cover
			const partBeforeCover = this.getGapPartBeforeCover(gap, cover);
			if (partBeforeCover) {
				result.push(partBeforeCover);
			}

			// Add part of gap after the cover
			const partAfterCover = this.getGapPartAfterCover(gap, cover);
			if (partAfterCover) {
				result.push(partAfterCover);
			}
		}

		return result;
	}

	/**
	 * Gets the portion of a gap that comes before a covering period.
	 *
	 * @param gap - The original gap
	 * @param cover - The covering period
	 * @returns The gap portion before the cover, or null if none exists
	 */
	private getGapPartBeforeCover(gap: TimePeriod, cover: TimePeriod): TimePeriod | null {
		const partEnd = dayjs.min(gap.end, cover.start.subtract(1, 'day'));

		if (gap.start.isSameOrBefore(partEnd)) {
			return { start: gap.start, end: partEnd };
		}

		return null;
	}

	/**
	 * Gets the portion of a gap that comes after a covering period.
	 *
	 * @param gap - The original gap
	 * @param cover - The covering period
	 * @returns The gap portion after the cover, or null if none exists
	 */
	private getGapPartAfterCover(gap: TimePeriod, cover: TimePeriod): TimePeriod | null {
		const partStart = dayjs.max(gap.start, cover.end.add(1, 'day'));

		if (partStart.isSameOrBefore(gap.end)) {
			return { start: partStart, end: gap.end };
		}

		return null;
	}

	/**
	 * Filters out invalid periods and sorts valid ones by start date.
	 *
	 * @param periods - Array of time periods to filter and sort
	 * @returns Filtered and sorted array of valid time periods
	 */
	private filterAndSortValidPeriods(periods: TimePeriod[]): TimePeriod[] {
		return periods.filter((p) => p.start.isSameOrBefore(p.end)).sort((a, b) => a.start.diff(b.start));
	}
}
